import React from "react";
import { useState, useEffect, useContext } from "react";
import {
  FileText,
  Users,
  Calendar,
  Award,
  Utensils,
  Martini,
  Drum,
  GraduationCap,
  Medal,
} from "lucide-react";
import WhiteBoxLayout from "../../components/WhiteBoxLayout/WhiteBoxLayout";
import "./style.css";
import RegistrationContext from "../../contexts/RegistrationContext";
import axios from "axios";
import { base_url } from "../../config";
import StudentProof from "./StudentProof";

const HelpModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div
        className="bg-white rounded-lg shadow-lg p-8 w-full max-w-4xl relative"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          ×
        </button>
        <div className="p-6 max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Instructions for Authors Attending the Conference</h1>
          <p className="mb-6">
            We are pleased to welcome you to our upcoming conference. Please carefully review the
            following registration guidelines, especially if you are an author of an accepted paper.
          </p>

          <h2 className="text-2xl font-semibold mb-4">Registration Categories</h2>

          <div className="mb-6">
            <h3 className="text-xl font-semibold">1. Regular (Full Delegate) Registration</h3>
            <p>
              Grants full access to the conference and allows the registrant to cover one or more papers,
              <strong> as long as all papers are authored by the registrant</strong>.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-semibold">2. Student Registration</h3>
            <p>
              Available at a discounted rate. <strong>This option does not include paper coverage.</strong>
            </p>
            <p className="mt-2">Suitable if:</p>
            <ul className="list-disc list-inside ml-4">
              <li>You are a student attending without presenting any papers, or</li>
              <li>You are a student author, but another co-author (not registering as a student) is covering the paper</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-semibold">3. Student with Paper Registration</h3>
            <p>
This registration type is required when a student is registering a paper and no co-author is registering it on their behalf.            </p>
            <p className="mt-2 text-red-600 font-medium">
              This category does not offer a discount since it includes paper registration.
            </p>
          </div>

          <h2 className="text-2xl font-semibold mb-4">Important Notes for Authors</h2>
          <ul className="list-disc list-inside ml-4 mb-6">
            <li>
              <strong>All accepted papers must be covered by a valid registration</strong> in order to be included in the
              conference proceedings.
            </li>
            <li>
              A single registration (Regular or Student with Paper) may cover multiple papers,
              provided all are authored by the registrant.
            </li>
            <li>
              Papers not covered by an eligible registration will be excluded from the official proceedings.
            </li>
          </ul>

          <div className="mb-6">
            <h3 className="text-xl font-semibold">4. Industry Day Registration</h3>
            <p>
              This registration type provides access to the <strong>Industry Day program only</strong>. It does not allow
              participation in the full conference or the ability to register papers.
            </p>
          </div>

        
        </div>
      </div>
    </div>
  );
};

const EligibilityTag: React.FC<{ who: string }> = ({ who }) => {
  if (who === "students") {
    return (
      <li className="flex items-start">
        <Users className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
        <span>Only full time students are eligible</span>
      </li>
    );
  }

  return (
    <li className="flex items-start">
      <Users className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Unrestricted category (all delegates eligible)</span>
    </li>
  );
};
const ReducedFeesTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <Award className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Reduced Fees</span>
    </li>
  );
};

const WorkshopsTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <Calendar className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Access to Selected Workshops (Additional Fees Apply)</span>
    </li>
  );
};
const TutorialsTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <GraduationCap className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Access to Selected Tutorials (Additional Fees Apply)</span>
    </li>
  );
};

const ReceptionTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <Martini className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Access to Reception</span>
    </li>
  );
};
const StudentReceptionTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <Drum className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Access to the Student Reception</span>
    </li>
  );
};
const BanquetTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <Utensils className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Access to the Banquet</span>
    </li>
  );
};

const CanPapersTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <FileText className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Can Register Papers</span>
    </li>
  );
};

const CantPapersTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <FileText className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Can't Register Papers</span>
    </li>
  );
};

const FreeTutorialsTag: React.FC = () => {
  return (
    <li className="flex items-start">
      <GraduationCap className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
      <span>Free Access to Tutorials</span>
    </li>
  );
};

const PpiTierBadge: React.FC<{ tier: string | null | undefined }> = ({ tier }) => {
  if (!tier) return null;
  const styles: Record<string, { color: string; label: string }> = {
    gold:   { color: "text-yellow-500",  label: "Gold" },
    silver: { color: "text-gray-400",    label: "Silver" },
    bronze: { color: "text-amber-700",   label: "Bronze" },
  };
  const s = styles[tier];
  if (!s) return null;
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-700"
      title={`PPI ${s.label} discount available`}
    >
      <Medal className={`h-3.5 w-3.5 ${s.color}`} aria-hidden="true" />
      PPI {s.label}
    </span>
  );
};

export default function RegistrationType() {
  const [, setFileError] = useState("");
  const {
    setError,
    reload,
    setReload,
    optionAccess,
    setOptionAccess,
    maxStep,
    setCart,
    registrationType,
    setRegistrationType,
    setMaxStep,
    data,
    ppiTier,
  } = useContext(RegistrationContext);
  const [fileUploaded, setFileUploaded] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  const [attendanceOption, setAttendanceOption] = useState(optionAccess);
  // Disable attendance option when Industry day is selected
  useEffect(() => {
    if (
      registrationType === "industry" ||
      registrationType === "student-paper"
    ) {
      setAttendanceOption("full");
    }
  }, [registrationType]);

  const handleNext = async () => {
    // Validate that student proof is uploaded if student option is selected
    if (
      (registrationType === "student" ||
        registrationType === "student-paper") &&
      !fileUploaded
    ) {
      setFileError("Please upload proof of student status");
      return;
    }
    try {
      await axios.post(`${base_url}/api/progress`, {});
      setReload(reload + 1);
    } catch (error: any) {
      setError("Unexpected error. " + error.response.data.message);
    }
  };

  const getPrice = async () => {
    try {
      const response = await axios.get(`${base_url}/api/get_cart`);
      setCart(response.data);
    } catch (error: any) {
      setError("Unexpected error. " + error.response.data.message);
    }
  };

  const updateType = async (type: string) => {
    setMaxStep("Registration Type");
    try {
      await axios.post(`${base_url}/api/update_type`, {
        type: type,
        option: attendanceOption,
      });
    } catch (error: any) {
      setError("Unexpected error. " + error.response.data.message);
    }
  };

  const updateRegistrationType = async (type: string) => {
    if (maxStep !== "Registration Type") {
      if (
        !window.confirm(
          "Are you sure you want to change the registration type? This will reset your registration."
        )
      ) {
        return;
      }
    }
    setRegistrationType(type);
    await updateType(type);
    await getPrice();
  };

  const updateOption = async (option: string) => {
    try {
      await axios.post(`${base_url}/api/update_type`, {
        type: registrationType,
        option: option,
      });
      setOptionAccess(option);
    } catch (error: any) {
      setError("Unexpected error. " + error.response.data.message);
    }
  };

  const updateAttendanceOption = async (option: string) => {
    setAttendanceOption(option);
    await updateOption(option);
    await getPrice();
  };

  return (
    <>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <WhiteBoxLayout
        title="Registration Type"
        handleSelect={() => handleNext()}
        disableNext={
          !registrationType ||
          ((registrationType === "student" ||
            registrationType === "student-paper") &&
            !fileUploaded)
        }
      >
        <div className="grid md:grid-cols-2 gap-6">

          {/* Left column - Registration options */}

          <div>
            <h1 className="text-xl font-bold mb-4">Registration Type
              <button
                type="button"
                className="ml-2 px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 text-sm font-semibold shadow hover:bg-gray-100 transition"
                onClick={() => setHelpOpen(true)}
              >
                Help for authors
              </button>
            </h1>
            <h2 className="text-lg font-semibold mb-4">
              Select Registration Category
            </h2>
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  type="radio"
                  id="regular"
                  name="registrationType"
                  value="regular"
                  checked={registrationType === "regular"}
                  onChange={() => updateRegistrationType("regular")}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="regular" className="ml-2 font-normal flex items-center flex-wrap">
                  Regular (Full Delegate) Registration
                  <PpiTierBadge tier={ppiTier} />
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="radio"
                  id="student"
                  name="registrationType"
                  value="student"
                  checked={registrationType === "student"}
                  onChange={() => {
                    updateRegistrationType("student");
                  }}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="student" className="ml-2 font-normal">
                  Student Registration
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="radio"
                  id="student-paper"
                  name="registrationType"
                  value="student-paper"
                  checked={registrationType === "student-paper"}
                  onChange={() => {
                    updateRegistrationType("student-paper");
                  }}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="student-paper" className="ml-2 font-normal">
                  Student Registration with Paper
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="radio"
                  id="industry"
                  name="registrationType"
                  value="industry"
                  checked={registrationType === "industry"}
                  onChange={() => updateRegistrationType("industry")}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="industry" className="ml-2 font-normal">
                  Industry Day
                </label>
              </div>

              {data.exhibitor_domain && (
                <div className="flex items-start">
                  <input
                    type="radio"
                    id="exhibitor"
                    name="registrationType"
                    value="exhibitor"
                    checked={registrationType === "exhibitor"}
                    onChange={() => updateRegistrationType("exhibitor")}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="exhibitor" className="ml-2 font-normal">
                    Exhibitor
                  </label>
                </div>
              )}
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-semibold mb-4">
                Select Your Attendance Option
              </h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <input
                    type="radio"
                    id="full"
                    name="attendanceOption"
                    value="full"
                    checked={attendanceOption === "full"}
                    onChange={() => updateAttendanceOption("full")}
                    disabled={
                      registrationType === "industry" ||
                      registrationType === "student-paper" ||
                      registrationType === "exhibitor"
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <label
                    htmlFor="full"
                    className={`ml-2 font-normal flex items-center flex-wrap ${registrationType === "industry" ||
                      registrationType === "student-paper" ||
                      registrationType === "exhibitor"
                      ? "text-gray-400"
                      : ""
                      }`}
                  >
                    Full Conference
                    {registrationType === "regular" && <PpiTierBadge tier={ppiTier} />}
                  </label>
                </div>

                <div className="flex items-start">
                  <input
                    type="radio"
                    id="workshops"
                    name="attendanceOption"
                    value="workshops"
                    checked={attendanceOption === "workshops"}
                    onChange={() => updateAttendanceOption("workshops")}
                    disabled={
                      registrationType === "industry" ||
                      registrationType === "student-paper" ||
                      registrationType === "exhibitor"
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <label
                    htmlFor="workshops"
                    className={`ml-2 font-normal ${registrationType === "industry" ||
                      registrationType === "student-paper" ||
                      registrationType === "exhibitor"
                      ? "text-gray-400"
                      : ""
                      }`}
                  >
                    Only Workshops / Tutorials / DC
                  </label>
                </div>
              </div>

              {registrationType === "industry" && (
                <p className="text-sm text-gray-500 mt-2 italic">
                  Attendance options are not applicable for industry day
                  registration
                </p>
              )}
              {registrationType === "student-paper" && (
                <p className="text-sm text-gray-500 mt-2 italic">
                  Attendance options are not applicable for students
                  registrations with paper
                </p>
              )}
            </div>
          </div>

          {/* Right column - Benefits */}
          <div className="bg-gray-50 p-5 rounded-lg border border-gray-100">
            {!registrationType && (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>Select a registration type to see benefits</p>
              </div>
            )}

            {registrationType === "regular" && attendanceOption === "full" && (
              <div className="space-y-4">
                <h3 className="font-medium text-blue-700">
                  Regular (Full delegate) Registration
                </h3>
                <ul className="space-y-3">
                  <EligibilityTag who={"any person"} />
                  <FreeTutorialsTag />
                  <WorkshopsTag />
                  <ReceptionTag />
                  <BanquetTag />
                  <CanPapersTag />
                </ul>
              </div>
            )}

            {registrationType === "regular" &&
              attendanceOption === "workshops" && (
                <div className="space-y-4">
                  <h3 className="font-medium text-blue-700">
                    Regular (Full delegate) Registration
                  </h3>
                  <ul className="space-y-3">
                    <EligibilityTag who={"any person"} />
                    <WorkshopsTag />
                    <TutorialsTag />
                  </ul>
                </div>
              )}

            {registrationType === "student" && attendanceOption === "full" && (
              <div className="space-y-4">
                <h3 className="font-medium text-blue-700">
                  Student Registration
                </h3>
                <ul className="space-y-3">
                  <EligibilityTag who={"students"} />
                  <ReducedFeesTag />
                  <FreeTutorialsTag />
                  <WorkshopsTag />
                  <ReceptionTag />
                  <StudentReceptionTag />
                  <CantPapersTag />
                  <StudentProof ready={setFileUploaded} />

                  {/* <FileUpload
                    label="Upload Student Proof"
                    description="Upload proof of student status (PDF, max 5MB)"
                    acceptedFileTypes={["application/pdf"]}
                    maxSize="5MB" /> */}
                </ul>
              </div>
            )}

            {registrationType === "student" &&
              attendanceOption === "workshops" && (
                <div className="space-y-4">
                  <h3 className="font-medium text-blue-700">
                    Student Registration
                  </h3>
                  <ul className="space-y-3">
                    <EligibilityTag who={"students"} />
                    <ReducedFeesTag />
                    <WorkshopsTag />
                    <StudentProof ready={setFileUploaded} />
                  </ul>
                </div>
              )}

            {registrationType === "student-paper" &&
              attendanceOption === "full" && (
                <div className="space-y-4">
                  <h3 className="font-medium text-blue-700">
                    Student Registration with paper
                  </h3>
                  <ul className="space-y-3">
                    <EligibilityTag who={"students"} />
                    <FreeTutorialsTag />
                    <WorkshopsTag />
                    <ReceptionTag />
                    <StudentReceptionTag />
                    <BanquetTag />
                    <CanPapersTag />
                    <StudentProof ready={setFileUploaded} />
                  </ul>
                </div>
              )}

            {registrationType === "industry" && (
              <div className="space-y-4">
                <h3 className="font-medium text-blue-700">Industry Day</h3>
                <ul className="space-y-3">
                  <EligibilityTag who={"any person"} />
                  <li className="flex items-start">
                    <Calendar className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Only access to the Industry Day</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </WhiteBoxLayout>
    </>
  );
}
